const DEFAULT_ERROR_MESSAGE = 'Something happened'
const USER_REJECTED_MESSAGE = 'Transaction cancelled in wallet.'
const LEDGER_ETH_APP_MESSAGE =
  'Please make sure your Ledger is connected, unlocked, and the Ethereum app is open.'

const USER_REJECTED_PATTERNS = [
  'user rejected the request',
  'user rejected transaction',
  'user rejected',
  'user denied transaction signature',
  'user denied message signature',
  'user denied account authorization',
  'rejected by user',
  'request rejected by the user',
  'transaction was rejected',
  'action rejected',
  'wallet rejected'
]

const MESSAGE_SECTION_MARKERS = [
  '\n\nContract Call:',
  '\n\nRequest Arguments:',
  '\n\nEstimate Gas Arguments:',
  '\n\nDetails:',
  '\n\nVersion:',
  '\nDetails:',
  '\nVersion:'
]

type ErrorRecord = Record<string, unknown>

function isErrorRecord(error: unknown): error is ErrorRecord {
  return typeof error === 'object' && error !== null
}

function getStringProperty(error: ErrorRecord, key: string) {
  const value = error[key]

  return typeof value === 'string' ? value : undefined
}

function getErrorCode(error: ErrorRecord) {
  const code = error.code

  return typeof code === 'number' || typeof code === 'string' ? code : undefined
}

function collectErrorChain(error: unknown, chain: unknown[] = []): unknown[] {
  if (chain.length >= 8 || chain.includes(error)) {
    return chain
  }

  chain.push(error)

  if (!isErrorRecord(error)) {
    return chain
  }

  const cause = error.cause

  if (cause) {
    collectErrorChain(cause, chain)
  }

  return chain
}

function getErrorMessages(error: unknown) {
  return collectErrorChain(error).flatMap((item) => {
    if (typeof item === 'string') {
      return [item]
    }

    if (!isErrorRecord(item)) {
      return []
    }

    return [
      getStringProperty(item, 'shortMessage'),
      getStringProperty(item, 'details'),
      getStringProperty(item, 'message'),
      getStringProperty(item, 'originalMessage'),
      getStringProperty(item, 'reason')
    ].filter((message): message is string => Boolean(message))
  })
}

function isUserRejectedError(error: unknown) {
  const messages = getErrorMessages(error)

  if (
    collectErrorChain(error).some((item) => {
      if (!isErrorRecord(item)) {
        return false
      }

      return (
        getErrorCode(item) === 4001 ||
        getErrorCode(item) === '4001' ||
        getStringProperty(item, 'name') === 'UserRejectedRequestError'
      )
    })
  ) {
    return true
  }

  return messages.some((message) => {
    const normalizedMessage = message.toLowerCase()

    return USER_REJECTED_PATTERNS.some((pattern) =>
      normalizedMessage.includes(pattern)
    )
  })
}

function isLedgerEthAppError(error: unknown) {
  return getErrorMessages(error).some((message) => {
    const normalizedMessage = message.toLowerCase()

    return (
      normalizedMessage.includes('ledger device') &&
      (normalizedMessage.includes('0x6511') ||
        normalizedMessage.includes('unknown_error'))
    )
  })
}

function extractContractRevertReason(message: string) {
  return message.match(/reverted with the following reason:\s*\n\s*([^\n]+)/i)?.[1]
}

function stripVerboseSections(message: string) {
  const markerIndex = MESSAGE_SECTION_MARKERS.reduce((index, marker) => {
    const nextIndex = message.indexOf(marker)

    if (nextIndex === -1) {
      return index
    }

    return index === -1 ? nextIndex : Math.min(index, nextIndex)
  }, -1)

  return markerIndex === -1 ? message : message.slice(0, markerIndex)
}

function sanitizeErrorMessage(message: string) {
  const contractRevertReason = extractContractRevertReason(message)

  return (contractRevertReason || stripVerboseSections(message))
    .replace(/^Error:\s*/i, '')
    .trim()
}

export function formatErrorMessage(
  error: unknown,
  fallback = DEFAULT_ERROR_MESSAGE
) {
  if (isUserRejectedError(error)) {
    return USER_REJECTED_MESSAGE
  }

  if (isLedgerEthAppError(error)) {
    return LEDGER_ETH_APP_MESSAGE
  }

  const [message] = getErrorMessages(error)
  const formattedMessage = message ? sanitizeErrorMessage(message) : ''

  return formattedMessage || fallback
}
